/**
 * F18 — NIPE tool: openOnboardWizard
 *
 * UI navigation tool. Asks the SPA to mount the F18 wizard at
 * `/onboard/new`. Idempotent: if the wizard is already mounted on the
 * given step, this is a no-op.
 */

import type { NipeTool } from './types';

export type OnboardWizardStep =
  | 'intro'
  | 'tenant'
  | 'consent'
  | 'signin'
  | 'firm'
  | 'byo'
  | 'provisioning';

export interface OpenOnboardWizardContext {
  /** Optional. Only present when the SPA wires the F18 wizard route. */
  openOnboardWizard?: (step?: OnboardWizardStep) => Promise<{
    opened: boolean;
    currentStep: OnboardWizardStep;
  }>;
}

const ALL_STEPS: OnboardWizardStep[] = [
  'intro',
  'tenant',
  'consent',
  'signin',
  'firm',
  'byo',
  'provisioning',
];

export const openOnboardWizardTool: NipeTool<OpenOnboardWizardContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'openOnboardWizard',
      description:
        'Open the customer onboarding wizard. Use when the user signals they want to set up a portal on their own SharePoint/M365 tenant.',
      parameters: {
        type: 'object',
        properties: {
          step: {
            type: 'string',
            description:
              'Optional step to jump to. One of intro, tenant, consent, signin, firm, provisioning. Defaults to intro.',
          },
        },
        required: [],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: false,
  handler: async (args, ctx) => {
    if (!ctx.openOnboardWizard) {
      return { error: 'Onboard wizard integration is not available in this session.' };
    }
    const step =
      typeof args.step === 'string' && (ALL_STEPS as string[]).includes(args.step)
        ? (args.step as OnboardWizardStep)
        : undefined;
    return await ctx.openOnboardWizard(step);
  },
};
